/**
 * Print Utilities for Bill Printing
 * Supports browser print, PDF generation, and direct printer connection guidance
 */

import logoImage from '../components/assets/battasesukhsagarlogo.png';

/**
 * Convert image to base64 data URL
 * @param {string} imagePath - Path to the image
 * @returns {Promise<string>} Base64 data URL
 */
const imageToBase64 = (imagePath) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      try {
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      } catch (error) {
        reject(error);
      }
    };
    img.onerror = reject;
    img.src = imagePath;
  });
};

/**
 * Print bill using browser's native print dialog
 * @param {HTMLElement} element - The element to print
 */
export const printBill = async (element) => {
  if (!element) {
    console.error('No element provided for printing');
    return;
  }

  // Convert logo to base64
  let logoBase64 = '';
  try {
    logoBase64 = await imageToBase64(logoImage);
  } catch (error) {
    console.warn('Could not load logo image:', error);
  }

  // Create a new window for printing
  const printWindow = window.open('', '_blank');
  
  // Get the HTML content
  const printContent = element.innerHTML;
  
  // Write the content to the new window
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Bill Print</title>
        <style>
          @media print {
            @page {
              size: 80mm auto; /* Thermal printer width */
              margin: 0;
            }
            body {
              margin: 0;
              padding: 10px;
              font-family: 'Courier New', monospace;
              font-size: 12px;
              line-height: 1.4;
            }
            * {
              box-sizing: border-box;
            }
            .no-print {
              display: none !important;
            }
          }
          @media screen {
            body {
              font-family: 'Courier New', monospace;
              font-size: 12px;
              padding: 20px;
              max-width: 300px;
              margin: 0 auto;
            }
          }
          .bill-header {
            text-align: center;
            border-bottom: 1px dashed #000;
            padding-bottom: 10px;
            margin-bottom: 10px;
          }
          .restaurant-logo {
            max-width: 60px;
            max-height: 60px;
            margin: 0 auto 10px;
            display: block;
          }
          .bill-item {
            display: flex;
            justify-content: space-between;
            margin: 5px 0;
          }
          .bill-total {
            border-top: 1px dashed #000;
            padding-top: 10px;
            margin-top: 10px;
            font-weight: bold;
          }
          .bill-footer {
            text-align: center;
            margin-top: 15px;
            padding-top: 10px;
            border-top: 1px dashed #000;
            font-size: 10px;
          }
        </style>
      </head>
      <body>
        ${logoBase64 ? `<div class="bill-header"><img src="${logoBase64}" alt="Restaurant Logo" class="restaurant-logo" /></div>` : ''}
        ${printContent}
      </body>
    </html>
  `);
  
  printWindow.document.close();
  
  // Wait for content to load, then print
  setTimeout(() => {
    printWindow.focus();
    printWindow.print();
    // Optionally close the window after printing
    // printWindow.close();
  }, 250);
};

/**
 * Generate PDF from bill data
 * @param {Object} billData - The bill data object
 * @param {string} filename - Optional filename for the PDF
 */
export const generatePDF = async (billData, filename = 'bill.pdf') => {
  try {
    // Dynamic import of jsPDF (install it: npm install jspdf)
    let jsPDF;
    try {
      // Dynamic import of jsPDF - this will show a webpack warning if jspdf is not installed
      // but the code will handle it gracefully at runtime
      const jsPDFModule = await import('jspdf');
      if (jsPDFModule?.jsPDF) {
        jsPDF = jsPDFModule.jsPDF;
      } else {
        throw new Error('jsPDF not available');
      }
    } catch (importError) {
      // If jsPDF is not installed, show helpful message
      console.warn('jsPDF library not found. PDF generation requires jsPDF library.');
      alert('PDF generation requires jsPDF library.\n\nPlease install it by running:\nnpm install jspdf\n\nFor now, you can use the Print button to print the bill.');
      return;
    }
    
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [80, 200] // Thermal printer size
    });

    // Set font
    doc.setFont('courier', 'normal');
    doc.setFontSize(10);

    let y = 10;

    // Add logo
    try {
      const logoBase64 = await imageToBase64(logoImage);
      // Add logo image (15mm width, auto height, centered)
      const logoWidth = 20;
      const logoHeight = (logoWidth * 0.75); // Maintain aspect ratio (adjust as needed)
      doc.addImage(logoBase64, 'PNG', (80 - logoWidth) / 2, y, logoWidth, logoHeight);
      y += logoHeight + 3;
    } catch (error) {
      console.warn('Could not load logo image for PDF:', error);
    }

    // Header
    doc.setFontSize(12);
    doc.setFont('courier', 'bold');
    doc.text('HOTEL BATTASE SUKHSAGAR', 40, y, { align: 'center' });
    y += 5;
    doc.setFontSize(8);
    doc.setFont('courier', 'normal');
    doc.text('Vani-saptashrungi gad Road', 40, y, { align: 'center' });
    y += 3;
    doc.text('Vani,Nashik', 40, y, { align: 'center' });
    y += 3;
    doc.text('Phone: +91-7744811143', 40, y, { align: 'center' });
    y += 5;
    doc.line(5, y, 75, y);
    y += 5;

    // Bill details
    doc.setFontSize(9);
    doc.text(`Bill No: ${billData.billNumber || 'N/A'}`, 5, y);
    y += 4;
    doc.text(`Date: ${billData.date || new Date().toLocaleDateString()}`, 5, y);
    y += 4;
    doc.text(`Time: ${billData.time || new Date().toLocaleTimeString()}`, 5, y);
    y += 4;
    doc.text(`Table: ${billData.tableNumber}`, 5, y);
    y += 4;
    doc.text(`Waiter: ${billData.waiterName}`, 5, y);
    y += 5;
    doc.line(5, y, 75, y);
    y += 5;

    // Items
    doc.setFontSize(8);
    billData.items.forEach((item) => {
      const itemText = `${item.quantity}x ${item.name}`;
      const itemPrice = `₹${(item.price * item.quantity).toFixed(2)}`;
      
      doc.text(itemText, 5, y);
      doc.text(itemPrice, 75, y, { align: 'right' });
      y += 4;
    });

    y += 3;
    doc.line(5, y, 75, y);
    y += 4;

    // Totals
    doc.setFontSize(9);
    doc.text(`Subtotal: ₹${billData.subtotal.toFixed(2)}`, 5, y);
    y += 4;
    doc.text(`GST (18%): ₹${billData.gst.toFixed(2)}`, 5, y);
    y += 4;
    if (billData.discount > 0) {
      doc.text(`Discount: -₹${billData.discount.toFixed(2)}`, 5, y);
      y += 4;
    }
    doc.setFont('courier', 'bold');
    doc.setFontSize(10);
    doc.text(`Total: ₹${billData.total.toFixed(2)}`, 5, y);
    y += 5;

    doc.line(5, y, 75, y);
    y += 5;

    // Footer
    doc.setFont('courier', 'normal');
    doc.setFontSize(8);
    doc.text('Thank you for visiting!', 40, y, { align: 'center' });
    y += 4;
    doc.text('Visit us again', 40, y, { align: 'center' });

    // Save the PDF
    doc.save(filename);
  } catch (error) {
    console.error('Error generating PDF:', error);
    // Fallback to browser print if jsPDF is not available
    alert('PDF generation requires jsPDF library. Please install it: npm install jspdf');
  }
};

/**
 * Print directly to a connected printer (requires backend support)
 * This is a placeholder function - actual implementation requires server-side code
 * @param {Object} billData - The bill data object
 * @param {string} printerName - Optional printer name
 */
export const printToPrinter = async (billData, printerName = null) => {
  try {
    // This would typically make an API call to your backend
    // which handles direct printer communication
    const response = await fetch('/api/print', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        billData,
        printerName,
      }),
    });

    if (!response.ok) {
      throw new Error('Print request failed');
    }

    return await response.json();
  } catch (error) {
    console.error('Error printing to printer:', error);
    // Fallback to browser print
    alert('Direct printer connection not available. Using browser print instead.');
    return null;
  }
};

/**
 * Format bill data for printing
 * @param {Object} order - The order object
 * @param {number} discount - Discount amount
 * @param {Function} calculateSubtotal - Function to calculate subtotal
 * @param {Function} calculateGST - Function to calculate GST
 * @param {Function} calculateTotal - Function to calculate total
 */
export const formatBillData = (order, discount, calculateSubtotal, calculateGST, calculateTotal) => {
  return {
    billNumber: `BILL-${order.id}-${Date.now()}`,
    date: new Date().toLocaleDateString('en-IN'),
    time: new Date().toLocaleTimeString('en-IN'),
    tableNumber: order.tableNumber,
    waiterName: order.waiterName,
    items: order.items,
    subtotal: calculateSubtotal(),
    gst: calculateGST(),
    discount: discount,
    total: calculateTotal(),
  };
};

